import { useState, useRef, useEffect, useCallback } from 'react';

interface UseConversationScrollProps {
	selectedClient: string;
	localMessages: any;
	isLoadingMoreMessages: boolean;
	loadMoreMessages?: (phoneNumber: string, cursor: string) => Promise<any[]>;
}

export const useConversationScroll = ({
	selectedClient,
	localMessages,
	isLoadingMoreMessages,
	loadMoreMessages,
}: UseConversationScrollProps) => {
	const [showScrollToBottom, setShowScrollToBottom] = useState(false);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const messagesContainerRef = useRef<HTMLDivElement>(null);
	const isLoadingRef = useRef<boolean>(false);
	const userScrolledUpRef = useRef<boolean>(false);
	const lastScrollTopRef = useRef<number>(0);
	const previousScrollHeightRef = useRef<number>(0);
	const isInitialLoadRef = useRef<boolean>(true);
	const isLoadingMoreTopRef = useRef<boolean>(false);

	const scrollToBottom = useCallback(() => {
		if (messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
			userScrolledUpRef.current = false;
		}
	}, []);

	const maintainScrollPosition = useCallback(() => {
		if (messagesContainerRef.current && previousScrollHeightRef.current > 0) {
			const container = messagesContainerRef.current;
			const newHeight = container.scrollHeight;
			const heightDiff = newHeight - previousScrollHeightRef.current;
			container.scrollTop = lastScrollTopRef.current + heightDiff;
			previousScrollHeightRef.current = newHeight;
		}
	}, []);

	const handleScroll = useCallback(() => {
		const container = messagesContainerRef.current;
		if (!container || !selectedClient || isLoadingRef.current) {
			return;
		}

		const { scrollTop, scrollHeight, clientHeight } = container;
		lastScrollTopRef.current = scrollTop;
		previousScrollHeightRef.current = scrollHeight;

		const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
		setShowScrollToBottom(!isAtBottom);

		if (scrollTop < scrollHeight - clientHeight - 200) {
			userScrolledUpRef.current = true;
		} else if (isAtBottom) {
			userScrolledUpRef.current = false;
		}

		const conversation = localMessages?.[selectedClient];
		const messages = conversation?.messages;

		if (
			scrollTop < 100 &&
			messages &&
			messages.length > 0 &&
			conversation.hasMoreMessages !== false &&
			loadMoreMessages &&
			!isLoadingMoreMessages
		) {
			const oldestMessage = messages[0];
			if (oldestMessage?.timestamp) {
				isLoadingRef.current = true;
				isLoadingMoreTopRef.current = true;

				loadMoreMessages(selectedClient, oldestMessage.timestamp)
					.then(() => {
						requestAnimationFrame(() => {
							maintainScrollPosition();
						});
					})
					.catch((error) => {
						console.error('Error loading messages:', error);
					})
					.finally(() => {
						isLoadingRef.current = false;
						isLoadingMoreTopRef.current = false;
					});
			}
		}
	}, [
		selectedClient,
		localMessages,
		isLoadingMoreMessages,
		loadMoreMessages,
		maintainScrollPosition,
	]);

	useEffect(() => {
		if (selectedClient) {
			setShowScrollToBottom(false);
			userScrolledUpRef.current = false;
			lastScrollTopRef.current = 0;
			previousScrollHeightRef.current = 0;
			isInitialLoadRef.current = true;
			isLoadingMoreTopRef.current = false;
		}
	}, [selectedClient]);

	useEffect(() => {
		const container = messagesContainerRef.current;
		if (container) {
			container.addEventListener('scroll', handleScroll, { passive: true });
			return () => container.removeEventListener('scroll', handleScroll);
		}
	}, [handleScroll]);

	useEffect(() => {
		const messages = localMessages?.[selectedClient]?.messages;
		if (messages && messages.length > 0) {
			if (isInitialLoadRef.current) {
				setTimeout(() => {
					scrollToBottom();
					isInitialLoadRef.current = false;
				}, 100);
			} else if (
				!userScrolledUpRef.current &&
				!isLoadingRef.current &&
				!isLoadingMoreTopRef.current
			) {
				setTimeout(() => scrollToBottom(), 100);
			}
		}
	}, [localMessages, selectedClient, scrollToBottom]);

	const conditionalScrollToBottom = useCallback(() => {
		if (!userScrolledUpRef.current) {
			setTimeout(() => scrollToBottom(), 100);
		}
	}, [scrollToBottom]);

	return {
		showScrollToBottom,
		messagesEndRef,
		messagesContainerRef,
		scrollToBottom,
		conditionalScrollToBottom,
		userScrolledUp: userScrolledUpRef.current,
	};
};
